/**
 * Себестоимость раздела до позиции сметы (БМ-01): плановая себестоимость выбранного варианта по позициям смет
 * раздела — материалы, оборудование, работы, накладные — рядом со сметной стоимостью и маржой позиции.
 */
import type { SectionItemCost } from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { api } from '../../lib/api';
import { tenge } from '../../lib/format';
import { Button } from '../../ui/button';
import { ErrorBox, Spinner } from '../../ui/feedback';

export function ItemCostPanel({ lineId }: { lineId: string }) {
  const [open, setOpen] = useState(false);
  const q = useQuery({
    queryKey: ['budget', 'items-cost', lineId],
    queryFn: () => api<SectionItemCost>(`/budget-lines/${lineId}/items-cost`),
    enabled: open,
  });
  return (
    <div className="flex flex-col gap-2">
      <Button className="min-h-11 self-start" variant="secondary" onClick={() => setOpen(!open)}>
        {open ? 'Скрыть позиции сметы' : 'Себестоимость по позициям сметы'}
      </Button>
      {open ? (
        q.isPending ? (
          <Spinner />
        ) : q.isError ? (
          <ErrorBox error={q.error} />
        ) : (
          <Rows c={q.data} />
        )
      ) : null}
    </div>
  );
}

function Rows({ c }: { c: SectionItemCost }) {
  if (c.source === 'none')
    return <p className="text-[14px] text-ink-2">К разделу не привязано ни одной позиции сметы.</p>;
  return (
    <div className="flex flex-col gap-2 text-[14px]">
      <p className="text-ink-2">
        {c.source === 'variant'
          ? `План варианта «${c.variantName}» разложен по позициям: материалы — по материалам позиций, оборудование — по оборудованию, работы — по ФОТ и механизмам, накладные — по прямым затратам.`
          : c.source === 'selected'
            ? `Выбран вариант «${c.variantName}» без расчёта по видам затрат: его сумма разложена по сметной стоимости позиций.`
            : 'Вариант исполнения не выбран: сумма раздела разложена по сметной стоимости позиций.'}{' '}
        План раздела — {tenge(c.plannedTotal)}
        {c.changesTotal ? `, в том числе утверждённые изменения бюджета ${tenge(c.changesTotal)}` : ''}.
        Сметная стоимость позиций — {tenge(c.estimateTotal)}.
        {c.truncated ? ` Показаны ${c.items.length} самых крупных по плану из ${c.itemsTotal}.` : ''}
      </p>
      {c.stale ? (
        <p className="rounded-md bg-warning-soft px-3 py-2">
          Сметы раздела изменились после расчёта варианта — пересчитайте вариант, чтобы расклад был по новой
          смете.
        </p>
      ) : null}
      <ul className="flex flex-col gap-2">
        {c.items.map((i) => (
          <li key={i.itemId} className="rounded-md border border-border px-3 py-2 break-words">
            <p className="font-medium">
              <Link
                to="/estimates/$estimateId"
                params={{ estimateId: i.estimateId }}
                className="inline-flex min-h-11 items-center text-accent underline-offset-2 hover:underline"
              >
                {i.estimateCode}, поз. {i.number}
              </Link>{' '}
              {i.code ? `${i.code} ` : ''}
              {i.name}
            </p>
            <p className="num text-ink-2">
              {i.quantity.toLocaleString('ru-RU')} {i.unit} · по смете {tenge(i.estimateAmount)}
            </p>
            <p className="num mt-1">
              план {tenge(i.planned)}
              {i.materials !== null
                ? ` (материалы ${tenge(i.materials)}, оборудование ${tenge(i.equipment)}, работы ${tenge(i.works)}, накладные ${tenge(i.overheads)}${i.changes ? `, изменения ${tenge(i.changes)}` : ''})`
                : i.changes
                  ? ` (в том числе изменения ${tenge(i.changes)})`
                  : ''}{' '}
              · маржа <b>{tenge(i.margin)}</b>
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
}
